import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// Factory del módulo Vuex de "Análisis de <X>" (drill-down de un mes desde la
// tabla de evolución). Costos y Gastos comparten el mismo shape de estado,
// getters y mutations; sólo difieren en el endpoint, el nombre de la action de
// fetch (que los tabs despachan), el prefijo de log y el mensaje de error.
//
// @param {object}   config
// @param {Function} config.apiFn          getCostsAnalysis / getExpensesAnalysis
//        (month, account, groupBy, selectedItems, accountLevel) → Promise<Response>
// @param {string}   config.fetchActionName  "fetchCostsAnalysis" / "fetchExpensesAnalysis"
// @param {string}   config.logPrefix        "[costsAnalysis]" / "[expensesAnalysis]"
// @param {string}   config.errorMessage     fallback de UI cuando el fetch falla
export function createAnalysisModule({ apiFn, fetchActionName, logPrefix, errorMessage }) {
    const createState = () => ({
        /** YYYY-MM de la celda que abrió el modal. Null cuando está cerrado. */
        month: null,
        /** Cuenta elegida en el estado (card 1) que recorta el ranking. Null = mes
         * completo (todas las contrapartes). Sólo recorta el ranking, no el estado. */
        account: null,
        /** Nivel de `account`: "2" (grupo level_2) o "3" (cuenta level_3). Null
         * cuando account es null. Decide cómo filtra el backend el ranking. */
        accountLevel: null,
        /** Nivel 2 de `account` cuando es una cuenta (level 3): desambigua
         * cuentas homónimas bajo distinto grupo. Null en los demás casos. */
        accountGroup: null,
        /** Categoría dinámica (reservada para paridad; no usada hoy). */
        groupBy: null,
        /**
         * Filtro multi-select por categoría: { tiendas: ["T1"], marcas: [...] }.
         * Recorta statement + ranking. Categoría sin entries = sin filtro.
         */
        selectedItems: {},
        /**
         * Payload del backend (frozen para opt-out del observer profundo de Vue 2).
         * El statement de Costos expone `margin` por grupo; el de Gastos expone
         * `efficiency` (gasto/ventas) — misma forma, distinta conclusión inline.
         * @type {object | null}
         */
        data: null,
        isLoading: false,
        error: null,
        /** Filtro de texto del ranking de contrapartes (case-insensitive). */
        counterpartsQuery: "",
    });

    const getters = {
        /**
         * Ranking de contrapartes filtrado por `counterpartsQuery`. Devuelve la
         * lista completa cuando la query está vacía.
         */
        filteredCounterparts: (state) => {
            const ranking = state.data?.counterparts_ranking ?? [];
            const q = state.counterpartsQuery.trim().toLowerCase();
            if (!q) return ranking;
            return ranking.filter((c) => c.name.toLowerCase().includes(q));
        },
    };

    const mutations = {
        SET_CONTEXT(state, { month, account, accountLevel, accountGroup, groupBy, selectedItems }) {
            state.month = month;
            state.account = account ?? null;
            state.accountLevel = accountLevel ?? null;
            state.accountGroup = accountGroup ?? null;
            state.groupBy = groupBy ?? null;
            state.selectedItems = Object.freeze(selectedItems ?? {});
        },
        // Cuenta elegida en el estado (card 1) para recortar el ranking, sin tocar
        // el statement/headline. account=null → todas las contrapartes del mes.
        SET_SELECTED_ACCOUNT(state, { account, level, group }) {
            state.account = account ?? null;
            state.accountLevel = level ?? null;
            state.accountGroup = group ?? null;
        },
        // Reemplaza sólo el ranking dentro del payload, preservando las refs de
        // statement/headline (así la card del estado no se repinta ni colapsa).
        SET_RANKING(state, ranking) {
            if (!state.data) return;
            state.data = Object.freeze({ ...state.data, counterparts_ranking: ranking ?? [] });
        },
        SET_SELECTED_ITEMS(state, selectedItems) {
            state.selectedItems = Object.freeze(selectedItems ?? {});
        },
        SET_DATA(state, payload) {
            state.data = payload;
        },
        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_COUNTERPARTS_QUERY(state, query) {
            state.counterpartsQuery = query;
        },
        RESET(state) {
            // Reusa el factory para no desincronizar el reset si se agrega un
            // campo al state (el listado manual se quedaba atrás silenciosamente).
            Object.assign(state, createState());
        },
    };

    const actions = {
        // Carga el análisis completo del mes (statement + headline + ranking).
        // El nombre se parametriza porque los tabs despachan fetchCostsAnalysis /
        // fetchExpensesAnalysis explícitamente.
        async [fetchActionName]({ commit }, { month, account = null, accountLevel = null, accountGroup = null, groupBy = null, selectedItems = null }) {
            commit("SET_CONTEXT", { month, account, accountLevel, accountGroup, groupBy, selectedItems });
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await apiFn(month, account, groupBy, selectedItems, accountLevel, accountGroup);
                commit("SET_DATA", Object.freeze(response.data));
            } catch (error) {
                consoleHandlerError({ error, prefix: logPrefix, silent: true });
                commit("SET_ERROR", extractErrorMessage(error, errorMessage));
            } finally {
                commit("SET_LOADING", false);
            }
        },

        // Recorta el ranking de contrapartes a la cuenta elegida en el estado
        // (card 1) sin recargar el modal completo: refetchea con el mismo mes/
        // filtros y reemplaza SÓLO counterparts_ranking, así statement y headline
        // quedan intactos (sin flash de "Cargando…"). Descarta respuestas obsoletas
        // (la última selección manda). account=null = todas las contrapartes.
        async selectRankingAccount({ state, commit }, { account = null, level = null, group = null }) {
            commit("SET_SELECTED_ACCOUNT", { account, level, group });
            try {
                const response = await apiFn(
                    state.month, account, state.groupBy, state.selectedItems, level, group,
                );
                if (state.account !== account || state.accountLevel !== level || state.accountGroup !== group) return;
                commit("SET_RANKING", response.data.counterparts_ranking);
            } catch (error) {
                consoleHandlerError({ error, prefix: logPrefix, silent: true });
            }
        },
    };

    return { namespaced: true, state: createState, getters, mutations, actions };
}
